import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import WifiIcon from '@mui/icons-material/Wifi';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { toast } from 'react-toastify';

// Use unified date utils
import { moment } from '../utils/dateUtils';

import { BrowserTitle, Spinner } from '../components';
import settingsServices from '../services/settingsServices';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Mailer = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // Email logs state
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  // Email config state
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(null); // 'customers' | 'suppliers' | null
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailTarget, setTestEmailTarget] = useState('customers'); // 'customers' | 'suppliers'
  
  const [emailConfig, setEmailConfig] = useState({
    enabled: true,
    // SMTP #1 - For Customers (Proposals)
    smtpServer: 'smtp.gmail.com',
    smtpPort: 587,
    smtpEncryption: 'tls',
    smtpEmail: '',
    smtpPassword: '',
    smtpFromName: 'Airmaxx Proposals',
    // SMTP #2 - For Suppliers (Equipment Orders)
    smtp2Server: 'smtp.gmail.com',
    smtp2Port: 587,
    smtp2Encryption: 'tls',
    smtp2Email: 'airmaxxequipment@gmail.com',
    smtp2Password: '',
    smtp2FromName: 'Airmaxx Equipment',
  });

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await settingsServices.getEmailLogs({
        page: page + 1,
        limit: rowsPerPage,
        search,
      });
      setEmails(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailConfig = async () => {
    setConfigLoading(true);
    try {
      const response = await settingsServices.getEmailConfig();
      if (response.data?.value) {
        setEmailConfig((prev) => ({ ...prev, ...response.data.value }));
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, [page, rowsPerPage]);

  useEffect(() => {
    // Load email config on component mount
    fetchEmailConfig();
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchEmails();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setEmailConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      console.log('[Mailer] Saving email config:', JSON.stringify(emailConfig, null, 2));
      const response = await settingsServices.updateEmailConfig(emailConfig);
      console.log('[Mailer] Save response:', response);
      toast.success('Email configuration saved successfully!');
      // Reload config from server to confirm save
      await fetchEmailConfig();
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error('Failed to save email configuration: ' + (error.response?.data?.message || error.message));
    } finally {
      setConfigSaving(false);
    }
  };

  const handleTestConnection = async (target) => {
    setTestingConnection(target);
    try {
      const response = await settingsServices.testEmailConnection(target);
      if (response.data.success) {
        toast.success(response.data.message || 'Connection successful!');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error(error.response?.data?.message || 'Connection test failed');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleOpenTestEmailDialog = (target) => {
    setTestEmailTarget(target);
    setTestEmailDialogOpen(true);
  };

  const handleCloseTestEmailDialog = () => {
    setTestEmailDialogOpen(false);
    setTestEmailAddress('');
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingTestEmail(true);
    try {
      const response = await settingsServices.sendTestEmail(testEmailAddress, testEmailTarget);
      if (response.data.success) {
        toast.success(response.data.message || 'Test email sent successfully!');
        handleCloseTestEmailDialog();
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  return (
    <>
      <BrowserTitle title="Airmaxx Pro | Email Management" />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Email Management
        </Typography>

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Email Logs" />
          <Tab label="Settings" />
        </Tabs>
      </Box>

      {/* Email Logs Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by email or proposal..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchEmails} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <Spinner />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Proposal ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sent By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No email logs found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      emails.map((email) => (
                        <TableRow key={email.id} hover>
                          <TableCell>
                            {moment(email.createdAt).tz('America/Los_Angeles').format('MM/DD/YYYY HH:mm')}
                          </TableCell>
                          <TableCell>
                            <Typography
                              component="a"
                              href={`/proposals/${email.proposal?.id}`}
                              sx={{ color: 'primary.main', textDecoration: 'none' }}
                            >
                              {email.proposal?.publicId || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {email.emailTo?.join(', ') || '-'}
                          </TableCell>
                          <TableCell>{email.subject || '-'}</TableCell>
                          <TableCell>{email.sentBy?.fullName || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={email.sentBy ? 'Sent' : 'Pending'}
                              color={email.sentBy ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel value={tabValue} index={1}>
        {configLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 900 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Email Configuration
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={emailConfig.enabled}
                    onChange={handleConfigChange('enabled')}
                  />
                }
                label="Email sending enabled"
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {/* SMTP #1 - Customers Card */}
                <Card 
                  sx={{ 
                    flex: 1, 
                    minWidth: 380,
                    border: '2px solid #1976d2',
                    position: 'relative'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        SMTP #1 — For Customers
                      </Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      Used for sending <strong>proposals to clients</strong>, password recovery emails, and other customer-facing communications.
                    </Alert>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="SMTP Server"
                        value={emailConfig.smtpServer}
                        onChange={handleConfigChange('smtpServer')}
                        size="small"
                        fullWidth
                      />
                      
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Port"
                          value={emailConfig.smtpPort}
                          onChange={handleConfigChange('smtpPort')}
                          size="small"
                          type="number"
                          sx={{ width: 100 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Encryption</InputLabel>
                          <Select
                            value={emailConfig.smtpEncryption}
                            onChange={handleConfigChange('smtpEncryption')}
                            label="Encryption"
                          >
                            <MenuItem value="ssl">SSL</MenuItem>
                            <MenuItem value="tls">TLS</MenuItem>
                            <MenuItem value="none">None</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <TextField
                        label="Email (Login)"
                        value={emailConfig.smtpEmail}
                        onChange={handleConfigChange('smtpEmail')}
                        size="small"
                        fullWidth
                      />

                      <TextField
                        label="Password"
                        value={emailConfig.smtpPassword}
                        onChange={handleConfigChange('smtpPassword')}
                        size="small"
                        type="password"
                        fullWidth
                        placeholder="••••••••"
                      />

                      <TextField
                        label="From Name"
                        value={emailConfig.smtpFromName}
                        onChange={handleConfigChange('smtpFromName')}
                        size="small"
                        fullWidth
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={testingConnection === 'customers' ? <CircularProgress size={16} /> : <WifiIcon />}
                          onClick={() => handleTestConnection('customers')}
                          disabled={testingConnection !== null}
                        >
                          Test Connection
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<SendIcon />}
                          onClick={() => handleOpenTestEmailDialog('customers')}
                        >
                          Send Test
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* SMTP #2 - Suppliers Card */}
                <Card 
                  sx={{ 
                    flex: 1, 
                    minWidth: 380,
                    border: '2px solid #ed6c02',
                    position: 'relative'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <LocalShippingIcon sx={{ color: '#ed6c02' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                        SMTP #2 — For Suppliers
                      </Typography>
                    </Box>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Used for sending <strong>equipment orders to suppliers</strong>. Keeps supplier communications separate from customer emails.
                    </Alert>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="SMTP Server"
                        value={emailConfig.smtp2Server}
                        onChange={handleConfigChange('smtp2Server')}
                        size="small"
                        fullWidth
                      />
                      
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Port"
                          value={emailConfig.smtp2Port}
                          onChange={handleConfigChange('smtp2Port')}
                          size="small"
                          type="number"
                          sx={{ width: 100 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Encryption</InputLabel>
                          <Select
                            value={emailConfig.smtp2Encryption}
                            onChange={handleConfigChange('smtp2Encryption')}
                            label="Encryption"
                          >
                            <MenuItem value="ssl">SSL</MenuItem>
                            <MenuItem value="tls">TLS</MenuItem>
                            <MenuItem value="none">None</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <TextField
                        label="Email (Login)"
                        value={emailConfig.smtp2Email}
                        onChange={handleConfigChange('smtp2Email')}
                        size="small"
                        fullWidth
                      />

                      <TextField
                        label="Password"
                        value={emailConfig.smtp2Password}
                        onChange={handleConfigChange('smtp2Password')}
                        size="small"
                        type="password"
                        fullWidth
                        placeholder="••••••••"
                      />

                      <TextField
                        label="From Name"
                        value={emailConfig.smtp2FromName}
                        onChange={handleConfigChange('smtp2FromName')}
                        size="small"
                        fullWidth
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={testingConnection === 'suppliers' ? <CircularProgress size={16} /> : <WifiIcon />}
                          onClick={() => handleTestConnection('suppliers')}
                          disabled={testingConnection !== null}
                        >
                          Test Connection
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={<SendIcon />}
                          onClick={() => handleOpenTestEmailDialog('suppliers')}
                        >
                          Send Test
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={configSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveConfig}
                disabled={configSaving}
              >
                Save Configuration
              </Button>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onClose={handleCloseTestEmailDialog}>
        <DialogTitle>
          Send Test Email — {testEmailTarget === 'customers' ? 'SMTP #1 (Customers)' : 'SMTP #2 (Suppliers)'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter the email address to send a test email using {testEmailTarget === 'customers' 
              ? <strong>{emailConfig.smtpEmail}</strong> 
              : <strong>{emailConfig.smtp2Email}</strong>
            }.
          </Typography>
          <TextField
            autoFocus
            label="Email Address"
            type="email"
            fullWidth
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestEmailDialog}>Cancel</Button>
          <Button 
            onClick={handleSendTestEmail} 
            variant="contained"
            color={testEmailTarget === 'customers' ? 'primary' : 'warning'}
            disabled={sendingTestEmail}
            startIcon={sendingTestEmail ? <CircularProgress size={16} /> : <SendIcon />}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Mailer;
